import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import axios from 'axios';

import i18n from '@/settings/i18n';
import { pages } from '@/settings/pages';
import { adminApi } from '@/services/api';
import { useCapyValues } from '@/hooks/useCapyValues';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useTurnstile } from '@/hooks/useTurnstile';
import { sentryReportError } from '@/utils/sentryReportError';

import { RemindForm, Values } from '../Presentation';

export const RemindFormContainer: React.FC = () => {
  const { flashError } = useFlashMessenger();
  const { setCapyValues, createParamsWithCapyValues } = useCapyValues();
  const {
    isLoading: turnstileLoading,
    turnstileError,
    onSuccess: onTurnstileSuccess,
    onError: onTurnstileError,
    resetError: resetTurnstileError,
    createParamsWithTurnstile,
    handleTurnstileApiError,
  } = useTurnstile();

  // Capyモード用（Capy廃止後に削除）
  const [capyLoading, setCapyLoading] = useState(false);
  const [capyError, setCapyError] = useState(false);

  /**
   * Feature Flag取得（Turnstile/Capy切り替え）
   * - useTurnstile: Turnstile有効（true=Turnstile）
   * - loading: API呼び出し中かどうか（true=読み込み中、false=完了）
   * loadingがtrueの間はSpinnerを表示し、APIレスポンス後に正しいCAPTCHAを表示
   */
  const { enabled: useTurnstileFlag, loading: featureFlagLoading } =
    useFeatureFlag('password_reset_turnstile_enabled');

  // 表示用ローディング状態
  const isLoading = turnstileLoading || capyLoading;

  const navigate = useNavigate();

  const onSubmit = useCallback(
    async (values: Values) => {
      try {
        // Capyモード用ローディング開始（Capy廃止後に削除）
        if (!useTurnstileFlag) {
          setCapyLoading(true);
        }
        setCapyError(false);
        resetTurnstileError();

        let data;

        if (useTurnstileFlag) {
          // Turnstileモード
          const result = createParamsWithTurnstile(values);
          if (!result.isValid) {
            return;
          }
          data = result.data;
        } else {
          // Capyモード
          data = createParamsWithCapyValues(values);
        }

        await adminApi.sendVerificationMail({ data });
        navigate(pages.token.sent);
      } catch (err) {
        if (!axios.isAxiosError(err)) {
          return;
        }

        // Turnstileエラー（400 + turnstile_error）の判定・処理
        // handled=true: Turnstileエラー、handled=false: 後続処理へ
        if (useTurnstileFlag) {
          const handled = handleTurnstileApiError(
            err.response?.status ?? 0,
            err.response?.data?.error,
          );
          if (handled) {
            return;
          }
        }

        // Capyエラー（Capy廃止後に削除）
        if (!useTurnstileFlag && err.response?.status === 401) {
          setCapyError(true);
          setCapyLoading(false);
          return;
        }

        // その他のエラー（Capy廃止後にsetCapyLoading削除）
        sentryReportError(err);
        flashError(i18n.t('ui.error.unexpected'));
        setCapyLoading(false);
      }
    },
    [
      useTurnstileFlag,
      setCapyLoading,
      createParamsWithTurnstile,
      createParamsWithCapyValues,
      handleTurnstileApiError,
      resetTurnstileError,
      flashError,
      navigate,
    ],
  );

  const childProps = {
    isLoading,
    capyError,
    onSubmit,
    setCapyValues,
    useTurnstile: useTurnstileFlag,
    turnstileError,
    onTurnstileSuccess,
    onTurnstileError,
    featureFlagLoading,
  };
  return <RemindForm {...childProps} />;
};
